import type { EvidenciaOut } from "@/lib/api";

export type ProgresoSubida = { nombre: string; pct: number } | null;

type Opciones = {
  casoId: number;
  file: File;
  /** Equipo al que se asocia la evidencia. null = general del caso. */
  imeiId?: number | null;
  /** Encolar el video a comprimir (no aplica a imágenes). */
  comprimir?: boolean;
  /** Etiquetas de origen; hoy: "bitacora" para las fotos de constancia. */
  tags?: string[];
  onProgreso?: (p: ProgresoSubida) => void;
};

/**
 * Sube UN archivo a las evidencias del caso y devuelve la evidencia creada.
 *
 * Va con XHR y no con `fetch` a propósito: fetch no reporta progreso de subida
 * y un crudo de video puede ser grande.
 *
 * Vive aquí y no dentro de una pestaña porque lo usan dos: la galería de
 * Evidencias y la zona de fotos de la bitácora. Copiar el pipeline de subida
 * ya salió mal una vez —`api/correos_adjuntos.py` lo duplicó y derivó— así que
 * del lado del front al menos el disparador es uno solo.
 *
 * ⚠️ El backend DEDUPLICA por sha256 y devuelve la evidencia EXISTENTE con 201
 * (no un 409): subir dos veces el mismo archivo regresa el mismo `id`.
 */
export function subirEvidencia({
  casoId,
  file,
  imeiId = null,
  comprimir = true,
  tags,
  onProgreso,
}: Opciones): Promise<EvidenciaOut> {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("comprimir", comprimir ? "true" : "false");
    if (imeiId !== null) fd.append("imei_id", String(imeiId));
    if (tags?.length) fd.append("tags", tags.join(","));

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/casos/${casoId}/evidencias`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgreso?.({ nombre: file.name, pct: Math.round((e.loaded / e.total) * 100) });
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as EvidenciaOut);
        } catch {
          reject(new Error("El servidor respondió algo que no se pudo leer"));
        }
        return;
      }
      let detail = xhr.statusText;
      try {
        detail = JSON.parse(xhr.responseText).detail || detail;
      } catch {
        /* respuesta no-JSON */
      }
      reject(new Error(detail));
    };
    xhr.onerror = () => reject(new Error("Error de red"));
    xhr.send(fd);
  });
}
